/**
 * File Handling Utilities for Form Builder
 * 
 * Handles image and logo file uploads/downloads for form elements.
 * These utilities manage the conversion between:
 * - Client-side: imageFile/logoFile (blob/base64 data)
 * - Server-side: imageFileId/logoFileId (file reference IDs)
 */

import { handleFileError } from './errorHandlers';
import { batchUploadImagesApi, decryptImageApi } from '@/api/formBuilder/imageApis';
import store from '@/store';

/**
 * Image/Logo Cache Map
 * Key: fileId (imageFileId or logoFileId)
 * Value: Blob object
 * Prevents redundant API calls for already fetched images
 */
const imageCacheMap = new Map();

/**
 * Extract all image and logo files from form elements
 * Used before saving form to upload files separately
 * Only extracts files that need uploading (new files without fileId)
 * 
 * @param {Array} formElements - Array of form elements
 * @returns {Object} - { filesToUpload: Array<{type, file, elementId}>, elementsWithFileIds: Array }
 */
export function extractFilesFromFormElements(formElements) {
  const filesToUpload = [];
  
  // Clone elements while preserving File/Blob objects
  // JSON.parse(JSON.stringify()) loses File objects, so we manually clone
  const elementsWithFileIds = formElements.map((element, index) => {
    const clonedElement = {
      ...element,
      data: { ...element.data }
    };
    
    // Handle Image element
    if (element.type === 'image' && element.data?.imageFile) {
      // Only upload if it's a new file (no imageFileId exists) and is file data
      if (!element.data.imageFileId && isFileData(element.data.imageFile)) {
        filesToUpload.push({
          type: 'image',
          file: element.data.imageFile,
          elementId: element.id,
          elementIndex: index
        });
      }
      // Remove the file data (only keep fileId for backend)
      delete clonedElement.data.imageFile;
    }

    // Handle ClinicBranding element
    if (element.type === 'clinic-branding' && element.data?.logoFile) {
      // Only upload if it's a new file (no logoFileId exists) and is file data
      if (!element.data.logoFileId && isFileData(element.data.logoFile)) {
        filesToUpload.push({
          type: 'logo',
          file: element.data.logoFile,
          elementId: element.id,
          elementIndex: index
        });
      }
      // Remove the file data (only keep fileId for backend)
      delete clonedElement.data.logoFile;
    }
    
    return clonedElement;
  });

  return { filesToUpload, elementsWithFileIds };
}

/**
 * Check if value is file data that needs uploading
 * Handles: File objects, Blob objects, blob URLs, and base64 data URLs
 * 
 * @param {String|File|Blob} value - Value to check
 * @returns {Boolean} - true if it's file data that needs uploading
 */
function isFileData(value) {
  if (!value) return false;
  
  // Check if it's a File object (from file input)
  if (value instanceof File) return true;
  
  // Check if it's a Blob object
  if (value instanceof Blob) return true;
  
  // Check string-based file data
  if (typeof value === 'string') {
    // Check for blob URL
    if (value.startsWith('blob:')) return true;
    
    // Check for base64 data URL
    if (value.startsWith('data:')) return true;
  }
  
  // Otherwise assume it's already a fileId or regular URL
  return false;
}

/**
 * Convert file data (File, Blob, blob URL, or base64) to File object
 * 
 * @param {String|File|Blob} data - File data to convert
 * @param {String} filename - Desired filename
 * @returns {Promise<File>} - File object
 */
async function dataUrlToFile(data, filename) {
  // If it's already a File object, return as-is
  if (data instanceof File) {
    return data;
  }
  
  // If it's a Blob, convert to File
  if (data instanceof Blob) {
    return new File([data], filename, { type: data.type });
  }
  
  // Handle string-based file data
  if (typeof data === 'string') {
    // If it's a blob URL, fetch it first
    if (data.startsWith('blob:')) {
      const response = await fetch(data);
      const blob = await response.blob();
      return new File([blob], filename, { type: blob.type });
    }
    
    // If it's a data URL, convert it
    if (data.startsWith('data:')) {
      const response = await fetch(data);
      const blob = await response.blob();
      return new File([blob], filename, { type: blob.type });
    }
  }
  
  throw new Error('Invalid file data format');
}

/**
 * Upload all files and update elements with fileIds
 * Uses batch upload API and Promise.allSettled for better error handling
 * Only uploads new files (those without existing fileIds)
 * 
 * @param {Array} filesToUpload - Array of {type, file, elementId, elementIndex}
 * @param {Array} elements - Form elements array to update
 * @returns {Promise<Array>} - Updated elements with fileIds
 */
export async function uploadFilesAndUpdateElements(filesToUpload, elements) {
  console.log('[FileHandlers] uploadFilesAndUpdateElements', filesToUpload, elements)
  
  // Clone elements - use shallow clone to preserve structure
  const updatedElements = elements.map(el => ({
    ...el,
    data: { ...el.data }
  }));

  // If no files to upload, return as-is
  if (filesToUpload.length === 0) {
    console.log('[FileHandlers] No new files to upload');
    return updatedElements;
  }

  try {
    // Convert all file data (blob URLs/base64) to File objects
    const fileConversionPromises = filesToUpload.map(async ({ file, type, elementIndex }) => {
      try {
        const fileObj = await dataUrlToFile(file, `${type}_${Date.now()}_${elementIndex}.png`);
        return { fileObj, type, elementIndex, success: true };
      } catch (error) {
        console.error(`[FileHandlers] Failed to convert ${type} for element at index ${elementIndex}:`, error);
        return { fileObj: null, type, elementIndex, success: false, error };
      }
    });

    const conversionResults = await Promise.allSettled(fileConversionPromises);
    
    // Filter successful conversions
    const filesToBatchUpload = [];
    const fileMetadata = [];
    
    conversionResults.forEach((result, index) => {
      if (result.status === 'fulfilled' && result.value.success && result.value.fileObj) {
        filesToBatchUpload.push(result.value.fileObj);
        fileMetadata.push({
          type: result.value.type,
          elementIndex: result.value.elementIndex
        });
      }
    });

    if (filesToBatchUpload.length === 0) {
      console.warn('[FileHandlers] No files successfully converted for upload');
      return updatedElements;
    }

    // Get clinic_id from store
    const userInfo = store.state.login?.userInfo;
    const clinicId = userInfo?.clinic_id;
    
    if (!clinicId) {
      throw new Error('Clinic ID not found. Please ensure you are logged in.');
    }

    // Batch upload all files
    console.log(`[FileHandlers] Uploading ${filesToBatchUpload.length} files via batch upload API`);
    const apiResponse = await batchUploadImagesApi(filesToBatchUpload, String(clinicId));
    
    console.log('[FileHandlers] API Response:', apiResponse);
    
    // Handle response - may be array directly or wrapped in an object
    const uploadResults = Array.isArray(apiResponse.items) ? apiResponse.items : [];
    
    if (!Array.isArray(uploadResults)) {
      console.error('[FileHandlers] Unexpected API response format:', apiResponse);
      throw new Error('Invalid API response format - expected array of file results');
    }
    console.log('[FileHandlers] Upload results:', uploadResults);
    
    // Update elements with fileIds
    uploadResults.forEach((result, index) => {
      const { type, elementIndex } = fileMetadata[index];
      const fileId = result.file_id || result.fileId || result.id;
      
      console.log(`[FileHandlers] Processing result ${index}:`, { result, type, elementIndex, fileId });
      
      if (fileId) {
        const element = updatedElements[elementIndex];
        
        if (!element) {
          console.error(`[FileHandlers] Element not found at index ${elementIndex}`);
          return;
        }
        
        if (type === 'image' && element.type === 'image') {
          element.data.imageFileId = fileId;
          console.log(`[FileHandlers] Assigned imageFileId ${fileId} to element at index ${elementIndex}`);
        } else if (type === 'logo' && element.type === 'clinic-branding') {
          element.data.logoFileId = fileId;
          console.log(`[FileHandlers] Assigned logoFileId ${fileId} to element at index ${elementIndex}`);
        }
      } else {
        console.warn(`[FileHandlers] No fileId found in result:`, result);
      }
    });

    console.log('[FileHandlers] Batch upload completed successfully');
    console.log('[FileHandlers] Updated elements with fileIds:', updatedElements);
    return updatedElements;
  } catch (error) {
    console.error('[FileHandlers] Batch upload failed:', error);
    handleFileError(error, 'upload', 'files');
    // Return elements as-is if upload fails completely
    return updatedElements;
  }
}

/**
 * Download file by fileId and convert to blob URL
 * Uses cache-first strategy to avoid redundant API calls
 * 
 * @param {String} fileId - File ID from backend
 * @returns {Promise<String>} - Blob URL
 */
export async function downloadFileById(fileId) {
  if (!fileId) {
    return 'https://via.placeholder.com/400x300?text=No+File+ID';
  }
  
  try {
    // Check cache first
    if (imageCacheMap.has(fileId)) {
      console.log(`[FileHandlers] Using cached image for fileId: ${fileId}`);
      const cachedBlob = imageCacheMap.get(fileId);
      return cachedBlob;
    }
    
    // Fetch from API if not cached
    console.log(`[FileHandlers] Fetching image from API for fileId: ${fileId}`);
    const blob = await decryptImageApi(fileId);
    
    // Cache the blob
    imageCacheMap.set(fileId, blob);
    
    // Return the blob directly
    return blob;
  } catch (error) {
    // Use silent mode - we don't want to spam user with notifications for each image
    handleFileError(error, 'download', 'file', { silent: true });
    // Return placeholder instead of throwing
    return `https://via.placeholder.com/400x300?text=Image+Not+Found`;
  }
}

/**
 * Convert fileIds in form elements to blob URLs
 * Used after loading form from backend
 * 
 * @param {Array} formElements - Array of form elements with fileIds
 * @returns {Promise<Array>} - Elements with blob URLs
 */
export async function convertFileIdsToBlobs(formElements) {
  const updatedElements = JSON.parse(JSON.stringify(formElements)); // Deep clone
  const downloadPromises = [];

  updatedElements.forEach((element, index) => {
    // Handle Image element
    if (element.type === 'image' && element.data?.imageFileId) {
      const promise = downloadFileById(element.data.imageFileId)
        .then(blobObj => {
          updatedElements[index].data.imageFile = blobObj;
        })
        .catch(error => {
          console.error(`[FileHandlers] Failed to download image for element ${element.id}:`, error);
          // Set placeholder on error
          updatedElements[index].data.imageFile = 'https://via.placeholder.com/400x300?text=Image+Not+Found';
        });
      downloadPromises.push(promise);
    }

    // Handle ClinicBranding element
    if (element.type === 'clinic-branding' && element.data?.logoFileId) {
      const promise = downloadFileById(element.data.logoFileId)
        .then(blobObj => {
          updatedElements[index].data.logoFile = blobObj;
          console.log(`[FileHandlers] Downloaded logo for element ${element.id}:`, blobObj, updatedElements[index]);

        })
        .catch(error => {
          console.error(`[FileHandlers] Failed to download logo for element ${element.id}:`, error);
          // Set placeholder on error
          updatedElements[index].data.logoFile = 'https://via.placeholder.com/200x60?text=Logo+Not+Found';
        });
      downloadPromises.push(promise);
    }
  });

  // Wait for all downloads to complete
  await Promise.all(downloadPromises);

  return updatedElements;
}


/**
 * Prepare form data for saving to backend
 * Extracts files, uploads them, and replaces with fileIds
 * 
 * @param {Object} formData - { name, description, category, emoji_tag, definition: { fields: [] } }
 * @returns {Promise<Object>} - Form data with fileIds instead of file blobs
 */
export async function prepareFormDataForSave(formData) {
  const { definition } = formData;
  
  if (!definition || !definition.fields) {
    return formData;
  }

  // Extract files from elements
  const { filesToUpload, elementsWithFileIds } = extractFilesFromFormElements(definition.fields);

  // If no files to upload, return as-is
  if (filesToUpload.length === 0) {
    return formData;
  }

  // Upload files and get updated elements with fileIds
  const updatedElements = await uploadFilesAndUpdateElements(filesToUpload, elementsWithFileIds);

  // Return updated form data
  return {
    ...formData,
    definition: {
      ...definition,
      fields: updatedElements
    }
  };
}

/**
 * Process form data after loading from backend
 * Converts fileIds to blob URLs for display
 * 
 * @param {Object} formData - Form data from backend with fileIds
 * @returns {Promise<Object>} - Form data with blob URLs
 */
export async function processFormDataAfterLoad(formData) {
  const { definition } = formData;
  
  if (!definition || !definition.fields) {
    return formData;
  }

  // Convert fileIds to blob URLs
  const updatedElements = await convertFileIdsToBlobs(definition.fields);

  // Return updated form data
  return {
    ...formData,
    definition: {
      ...definition,
      fields: updatedElements
    }
  };
}
